/* CBH — Scrolling text loop (seamless + auto-repeat enough to fill viewport) */
(() => {
  const SEL = '[data-cbh-marquee]';

  const reduced = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (root.dataset.cbhInit === '1') return;
    root.dataset.cbhInit = '1';

    const track = root.querySelector('[data-marquee-track]');
    const group1 = root.querySelector('[data-marquee-group]');
    const viewport = root.querySelector('.cbh-marquee__viewport');
    const btn = root.querySelector('.cbh-marquee__toggle');

    if (!track || !group1 || !viewport) return;

    const setPaused = (val) => {
      root.dataset.paused = val ? 'true' : 'false';
      if (btn) btn.setAttribute('aria-pressed', val ? 'true' : 'false');
      if (btn) {
        const label = btn.querySelector('.cbh-marquee__toggle-label');
        if (label) label.textContent = val ? 'Play' : 'Pause';
      }
    };

    // Reduced motion: start paused
    if (reduced()) setPaused(true);

    // Toggle button
    if (btn) {
      btn.addEventListener('click', () => setPaused(root.dataset.paused !== 'true'));
    }

    // Pause on hover/focus (optional)
    const pauseHover = root.dataset.pausehover !== 'false';
    if (pauseHover) {
      root.addEventListener('pointerenter', () => setPaused(true));
      root.addEventListener('pointerleave', () => { if (!reduced()) setPaused(false); });
      root.addEventListener('focusin', () => setPaused(true));
      root.addEventListener('focusout', () => { if (!reduced()) setPaused(false); });
    }

    const disableFocusIn = (el) => {
      el.querySelectorAll('a, button, input, select, textarea').forEach((node) => {
        node.setAttribute('tabindex', '-1');
      });
    };

    const rebuild = () => {
      const html = group1.innerHTML.trim();
      if (!html) return;

      // Measure base set width (use scrollWidth for stability)
      const setW = Math.ceil(group1.scrollWidth);
      const vw = Math.ceil(viewport.clientWidth || document.documentElement.clientWidth);

      if (!setW || setW < 10) return;

      // Remove any previous clones *after* measurement to avoid forcing layout.
      track.querySelectorAll('.cbh-marquee__group[aria-hidden="true"]').forEach((n) => n.remove());

      // Ensure total track length comfortably covers the viewport while moving
      // (2x viewport is usually enough; we add 1 extra set for safety)
      const desired = Math.max(vw * 2, setW * 2);
      let copies = Math.ceil(desired / setW) + 1;
      copies = Math.max(2, Math.min(10, copies)); // cap for sanity

      for (let i = 1; i < copies; i++) {
        const clone = document.createElement('div');
        clone.className = 'cbh-marquee__group';
        clone.setAttribute('aria-hidden', 'true');
        clone.innerHTML = html;
        disableFocusIn(clone);
        track.appendChild(clone);
      }

      // Animate by exactly ONE set width (seamless because sets are identical)
      const speed = Math.max(
        30,
        parseFloat(getComputedStyle(root).getPropertyValue('--cbh-marquee-speed')) || 70
      );

      root.style.setProperty('--cbh-marquee-distance', `${setW}px`);
      root.style.setProperty('--cbh-marquee-duration', `${Math.max(6, setW / speed)}s`);

      // Helpful for debugging in console
      root.dataset.cbhCopies = String(copies);
      root.dataset.cbhSetWidth = String(setW);
      root.dataset.cbhViewport = String(vw);
    };

    // Measure after layout + after fonts (debounced to avoid forced reflow)
    let cbhMarqueeMeasureToken = null;
    const measureSoon = () => {
      if (cbhMarqueeMeasureToken) {
        if (window.cancelIdleCallback && cbhMarqueeMeasureToken.kind === 'idle') cancelIdleCallback(cbhMarqueeMeasureToken.id);
        if (cbhMarqueeMeasureToken.kind === 'raf') cancelAnimationFrame(cbhMarqueeMeasureToken.id);
      }
      if (window.requestIdleCallback) {
        const id = requestIdleCallback(() => rebuild(), { timeout: 1500 });
        cbhMarqueeMeasureToken = { kind: 'idle', id };
      } else {
        const id = requestAnimationFrame(rebuild);
        cbhMarqueeMeasureToken = { kind: 'raf', id };
      }
    };
    measureSoon();
    window.addEventListener('load', measureSoon, { once: true });
    window.addEventListener('resize', measureSoon, { passive: true });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measureSoon).catch(() => {});
    }

    // React to size changes more reliably than resize alone
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => measureSoon());
      ro.observe(viewport);
      ro.observe(group1);
    }
  }

  const boot = (scope = document) => scope.querySelectorAll(SEL).forEach(init);

  function scheduleInit() {
  var started = false;
  var start = function(){
    if (started) return;
    started = true;
    var run = function(){ try { initAll(document); } catch(e){} };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 2500 });
    } else {
      setTimeout(run, 250);
    }
  };

  if ('IntersectionObserver' in window) {
    var els = document.querySelectorAll(SEL);
    if (els && els.length) {
      var cbhMarqueeIO = new IntersectionObserver(function(entries){
        for (var i=0;i<entries.length;i++){
          if (entries[i].isIntersecting) { cbhMarqueeIO.disconnect(); start(); break; }
        }
      }, { rootMargin: '300px 0px' });
      for (var j=0;j<els.length;j++){ cbhMarqueeIO.observe(els[j]); }
      setTimeout(start, 2800);
      return;
    }
  }
  start();
}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
